import React, {createContext, useContext, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Form, FormikProvider, useFormik} from 'formik';
import {useNavigate} from 'react-router-dom';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useAuthContext} from '../../../../common/components/core/AuthContext';
import {setToken, setUser} from '../../redux/actions/authActions';
import {AppDispatch} from '../../../../store/configureStore';
import {
  actionLogMessages as M,
  actionPages as AP, actionResults as R,
  actionSubjects as S,
  actionTypes as T,
  pathnames as P
} from '../../../../common/enums';
import {getErrorMessage} from '../../../../common/utils';

export type LoginByPhoneContext = {
  gotCode: boolean
};

export type LoginByPhoneContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: LoginByPhoneContext = {
  gotCode: false
};

export type LoginByPhoneFormValues = {
  phone: string
  code: string
}

export const defaultFormValues: LoginByPhoneFormValues = {
  phone: '',
  code: '',
};

export const LoginByPhoneContext = createContext<LoginByPhoneContext>(defaultContext);
LoginByPhoneContext.displayName = 'LoginByPhoneContext';

export const useLoginByPhoneContext = () => useContext(LoginByPhoneContext);

export const LoginByPhoneContextProvider = ({children}: LoginByPhoneContextProviderProps) => {
  const [gotCode, setGotCode] = useState(false);
  const {apiService} = useGlobalContext();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const {handleLogin} = useAuthContext();

  const formik = useFormik<LoginByPhoneFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof LoginByPhoneFormValues]: string }> = {};
      if (!values.phone) errors.phone = E.requiredField;
      if (gotCode && !values.code) errors.code = E.requiredField;

      return errors;
    },
    onSubmit: async (values) => {
      if (!gotCode) {
        try {
          const result = await apiService.getCode(values.phone);
          if (result.errorCode < 0) {
            globalMessage.open(getErrorMessage(result.errorCode, E));
          } else {
            setGotCode(true);
          }
        } catch (e) {
          globalMessage.open(E.serverError);
          setGotCode(false);
        }
      } else {
        try {
          const result = await apiService.confirmCode(values.phone, values.code);
          const token = result.body.token;
          const user = result.body.user;
          if (result.errorCode < 0) {
            globalMessage.open(getErrorMessage(result.errorCode, E));
          } else {
            if (token) {
              dispatch(setToken(token));
              apiService.setToken(token);
            }
            if (user) {
              dispatch(setUser(user));
            }
            await handleLogin();
            navigate(P.HOME);

            // if (token && user) {
            //   await apiService.logUserAction(
            //     AP.AUTH,
            //     S.LOGIN_BY_PHONE,
            //     T.GENERAL,
            //     M.LOGIN_BY_PHONE(values.phone),
            //     result.errorCode === 0 ? R.SUCCESS : R.FAIL
            //   )
            // }
          }
        } catch (e) {
          globalMessage.open(E.serverError);
          dispatch(setToken(''));
          dispatch(setUser(null));
        }
      }

    },
    enableReinitialize: true,
  });

  const context: LoginByPhoneContext = {
    ...defaultContext,
    gotCode
  };

  return (
    <LoginByPhoneContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </LoginByPhoneContext.Provider>
  );
};
